import React, { useState } from 'react';
import { 
  Type, 
  Palette, 
  Maximize2, 
  Layout, 
  Image as ImageIcon,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';
import TextEditor from './propertyEditors/TextEditor';
import ColorEditor from './propertyEditors/ColorEditor';
import SpacingEditor from './propertyEditors/SpacingEditor';
import TypographyEditor from './propertyEditors/TypographyEditor';
import LayoutEditor from './propertyEditors/LayoutEditor';
import ImageEditor from './propertyEditors/ImageEditor';

/**
 * Inspector Panel - Right sidebar showing editable properties
 */
export default function InspectorPanel({ 
  selectedElement, 
  onUpdateProperty, 
  onClose 
}) {
  const [expandedSections, setExpandedSections] = useState({
    text: true,
    typography: true,
    colors: true,
    spacing: true,
    layout: true,
    image: true,
  });

  if (!selectedElement) {
    return (
      <div className="w-full bg-white p-6 flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <Layout className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Select an element to edit</p>
        </div>
      </div>
    );
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const { editableProperties } = selectedElement;

  return (
    <div className="w-full bg-white flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Inspector</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {selectedElement.tagName}
            {selectedElement.dataType && ` • ${selectedElement.dataType}`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title="Close inspector"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Property Editors */}
      <div className="flex-1 overflow-y-auto">
        {/* Text Content Editor */}
        {editableProperties.text && (
          <PropertySection
            title="Content"
            icon={<Type className="w-4 h-4" />}
            isExpanded={expandedSections.text}
            onToggle={() => toggleSection('text')}
          >
            <TextEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}

        {/* Typography Editor */}
        {(editableProperties.fontSize || editableProperties.fontWeight || editableProperties.fontFamily) && (
          <PropertySection
            title="Typography"
            icon={<Type className="w-4 h-4" />}
            isExpanded={expandedSections.typography}
            onToggle={() => toggleSection('typography')}
          >
            <TypographyEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}

        {/* Color Editor */}
        {(editableProperties.color || editableProperties.backgroundColor) && (
          <PropertySection
            title="Colors"
            icon={<Palette className="w-4 h-4" />}
            isExpanded={expandedSections.colors}
            onToggle={() => toggleSection('colors')}
          >
            <ColorEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}

        {/* Spacing Editor */}
        {editableProperties.spacing && (
          <PropertySection
            title="Spacing"
            icon={<Maximize2 className="w-4 h-4" />}
            isExpanded={expandedSections.spacing}
            onToggle={() => toggleSection('spacing')}
          >
            <SpacingEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}

        {/* Layout Editor */}
        {editableProperties.layout && (
          <PropertySection
            title="Layout"
            icon={<Layout className="w-4 h-4" />}
            isExpanded={expandedSections.layout}
            onToggle={() => toggleSection('layout')}
          >
            <LayoutEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}

        {/* Image Editor */}
        {editableProperties.image && (
          <PropertySection
            title="Image"
            icon={<ImageIcon className="w-4 h-4" />}
            isExpanded={expandedSections.image}
            onToggle={() => toggleSection('image')}
          >
            <ImageEditor
              element={selectedElement}
              onUpdate={onUpdateProperty}
            />
          </PropertySection>
        )}
      </div>

      {/* Footer Info */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 mb-1">
          {selectedElement.dataPath ? (
            <>Path: <code className="text-xs bg-gray-200 px-1 rounded">{selectedElement.dataPath}</code></>
          ) : (
            'Direct element editing'
          )}
        </p>
        <p className="text-xs text-gray-400">
          {selectedElement.classList?.length > 0 && (
            <>Classes: {selectedElement.classList.slice(0, 3).join(', ')}</>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Collapsible property section
 */
function PropertySection({ title, icon, isExpanded, onToggle, children }) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-4 py-3 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );
}

